import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export function formatPrice({
    amount,
    currencyCode,
    locale,
    fractions = 2
}: {
    amount: number;
    currencyCode: string;
    locale: string;
    fractions: number;
}) {
    const validFractions = (fractions > 20 || fractions < 0 || !Number.isFinite(fractions)) ? 2 : fractions;

    const formatCurrency = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: validFractions,
        maximumFractionDigits: validFractions,
    });

    return formatCurrency.format(amount);
}

export function formatVariantPrice({
    amount,
    baseAmount,
    currencyCode,
    locale,
    fractions = 2
}: {
    baseAmount: number;
    amount: number;
    currencyCode: string;
    locale: string;
    fractions: number;
}) {
    const hasDiscount = baseAmount < amount;
    const formatDiscount = new Intl.NumberFormat(locale, { style: 'percent' });
    const discount = hasDiscount
        ? formatDiscount.format((amount - baseAmount) / amount)
        : null;

    const price = formatPrice({ amount, currencyCode, locale, fractions });
    const basePrice = hasDiscount
        ? formatPrice({ amount: baseAmount, currencyCode, locale, fractions })
        : null;

    return { price, basePrice, discount };
}

type PriceProps = {
    amount: number;
    baseAmount?: number;
    currencyCode?: string;
};

export default function usePrice(data?: PriceProps | null) {
    const { settings, settingsLoading, settingserror } = useSelector((state: any) => state.customer);
    const currencyOptions = settings?.options?.currencyOptions;
    const currency = settings?.options?.currency;
    const formation = currencyOptions?.formation;
    const fractions = currencyOptions?.fractions ?? 2;
    const { amount, baseAmount, currencyCode = currency } = data ?? {};
    const locale = formation ?? settings?.options.defaultLanguage;
    const value = useMemo(() => {
        if (typeof amount !== 'number' || !currencyCode) return '';

        return baseAmount
            ? formatVariantPrice({ amount, baseAmount, currencyCode, locale, fractions })
            : formatPrice({ amount, currencyCode, locale, fractions });
    }, [amount, baseAmount, currencyCode]);

    return typeof value === 'string'
        ? { price: value, basePrice: null, discount: null }
        : value;
}

